// Virtual waiting-room status. For now this SIMULATES a descending queue so the
// page is fully demoable; the real implementation polls a `/queue/status`
// endpoint that returns the same shape (the server hands out admission slots as
// capacity frees). Keep the return shape stable when wiring the real endpoint.

import { useEffect, useRef, useState } from 'react';

export type QueuePhase = 'connecting' | 'queued' | 'almost' | 'admitted';

export type QueueStatus = {
  phase: QueuePhase;
  position: number;
  initialPosition: number;
  peopleAhead: number;
  etaSeconds: number;
  /** 0..1, monotonically increasing (never moves backward) */
  progress: number;
};

// Average clearance rate of the simulated queue, used for the ETA estimate.
const CLEAR_PER_SEC = 7;

export function useQueueStatus(): QueueStatus {
  const initial = useRef(900 + Math.floor(Math.random() * 700)); // 900–1600
  const [position, setPosition] = useState(initial.current);
  const [phase, setPhase] = useState<QueuePhase>('connecting');

  useEffect(() => {
    let alive = true;
    let pos = initial.current;
    let stepTimer: ReturnType<typeof setTimeout>;

    const connect = setTimeout(() => {
      if (alive) setPhase('queued');
    }, 700);

    const step = () => {
      if (!alive) return;
      // Usually advance a few places; occasionally plateau (feels real).
      const drop = Math.random() < 0.18 ? 0 : 1 + Math.floor(Math.random() * 14);
      pos = Math.max(0, pos - drop);
      setPosition(pos);
      if (pos <= 0) {
        setPhase('admitted');
        return;
      }
      setPhase(pos <= 12 ? 'almost' : 'queued');
      stepTimer = setTimeout(step, 500 + Math.random() * 700);
    };
    stepTimer = setTimeout(step, 1100);

    return () => {
      alive = false;
      clearTimeout(connect);
      clearTimeout(stepTimer);
    };
  }, []);

  const peopleAhead = Math.max(0, position - 1);
  const etaSeconds = Math.round(position / CLEAR_PER_SEC);
  const progress = initial.current > 0 ? 1 - position / initial.current : 1;

  return { phase, position, initialPosition: initial.current, peopleAhead, etaSeconds, progress };
}
